import { CriterionEditor } from '@/components/admin/criteres/criterion-editor';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, fetchCriterion, fetchCriterionVersion } from '@/lib/api';
import { readCopyFlag, useConsumedCopyFlag } from '@/lib/copy-search';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * L'éditeur d'un critère existant (refonte modulaire §4, §6; maquette
 * `Editeur`).
 *
 * ## Two queries, two shapes, two key prefixes (issue #16)
 *
 * `['criterion', email, id]` holds a `CriterionDetail`; `['criterion-version',
 * email, id, versionId]` holds a `CriterionVersion`. Different prefixes on
 * purpose: the trap of #16 is two readers agreeing on a key and disagreeing on
 * what is under it, and `useQuery` is generic per component so nothing would
 * catch the drift. Neither key overlaps `['criteria', email]`, which the list
 * fills with the card projection.
 *
 * ## « Voir » travels in the URL
 *
 * `?version=<id>` rather than component state, the régime of every filtered
 * screen here: a version somebody is reading is exactly what gets pasted into
 * a thread with « regarde la v3 ». The editor renders read-only while it is
 * set (§4: « "Voir", qui ouvre une version en lecture seule dans l'éditeur »),
 * and `validateSearch` never throws - an unknown parameter degrades to « no
 * version » rather than to a blank screen.
 *
 * ## `key` on the editor, and what it must NOT contain
 *
 * The editor initialises its draft state from `detail` ONCE, with no effect
 * copying a query into state. So a reload of a stale draft has to be a
 * REMOUNT - without one, the box would refetch and the stale draft would stay
 * on screen, which is the one thing that box exists to fix.
 *
 * The key is therefore the criterion's id plus a COUNTER this screen owns, and
 * that shape is the whole point. The obvious spelling - keying on
 * `draft.updatedAt` - is a defect: every autosave answers with a new stamp and
 * writes it into this query, so the key would change roughly once a second
 * while somebody types, remounting the textarea under their caret and resetting
 * the « autosauvegardé à » line the autosave had just set. A counter cannot be
 * moved by a data update, only by the gesture that asks for a reload; and the
 * bump waits for the refetch to LAND, because remounting before the fresh
 * answer arrives would re-seed the editor from the very draft it is escaping.
 */

type CriterionSearch = { version?: string; copie?: true };

function readSearch(search: Record<string, unknown>): CriterionSearch {
  const raw = search.version;
  const parsed: CriterionSearch = typeof raw === 'string' && raw !== '' ? { version: raw } : {};
  const copie = readCopyFlag(search.copie);
  // OMITTED and never `undefined`: TanStack writes every key it is handed into
  // the address bar, so a present key would put `?copie=` on every editor.
  if (copie !== undefined) parsed.copie = copie;
  return parsed;
}

export const Route = createFileRoute('/admin/criteres/$criterionId')({
  validateSearch: readSearch,
  component: CriterionEditorPage,
});

function CriterionEditorPage() {
  const { email } = useAdminSession();
  const { criterionId } = Route.useParams();
  const { version, copie } = Route.useSearch();
  const navigate = useNavigate();

  /** Bumped by « Recharger le brouillon du serveur », and by nothing else. */
  const [reload, setReload] = useState(0);

  const criterion = useQuery({
    queryKey: ['criterion', email, criterionId],
    queryFn: () => fetchCriterion(criterionId),
    retry: false,
  });

  const viewed = useQuery({
    queryKey: ['criterion-version', email, criterionId, version ?? null],
    queryFn: () => fetchCriterionVersion(criterionId, version ?? ''),
    enabled: version !== undefined,
    retry: false,
  });

  /*
    The flag is CONSUMED once the editor it describes has OPENED (§4.2).

    Two halves, and both matter. Left in the address bar it would re-select
    the name field at every later remount - and the counter just above is
    one: « Recharger le brouillon du serveur » bumps it. Dropped too EARLY it
    would never select anything at all: the editor reads the flag on ITS
    mount, and it does not mount until the entity has been read - so this
    waits for the entity, and React runs a child's effects before its
    parent's, which is what puts the selection before the drop.

    `replace`, because it is a hand-off between two screens and not a place
    anybody goes back to.
  */
  useConsumedCopyFlag(copie, criterion.data !== undefined, () =>
    navigate({
      to: '/admin/criteres/$criterionId',
      params: { criterionId },
      // Rebuilt from what THIS route holds rather than reduced from the
      // previous search: TanStack types that argument as the union of every
      // route's shape, and a key OMITTED is the only spelling
      // `exactOptionalPropertyTypes` accepts.
      search: version === undefined ? {} : { version },
      replace: true,
    }),
  );

  const rejected = useRejectOn401(criterion.error);
  const missing = criterion.error instanceof ApiError && criterion.error.status === 404;

  return (
    <div className="space-y-6">
      <p className="text-gray-600 text-sm">
        <Link to="/admin/criteres" className="text-bleu-nuit underline underline-offset-2">
          Critères
        </Link>
        <span className="mx-2">›</span>
        {criterion.data?.name ?? '...'}
      </p>

      {criterion.isPending ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement du critère" />
        </div>
      ) : null}

      {missing ? (
        <p className="text-gray-700">
          Ce critère n’existe pas.{' '}
          <Link to="/admin/criteres" className="text-bleu-nuit underline underline-offset-2">
            Revenir à la bibliothèque
          </Link>
          .
        </p>
      ) : null}

      {criterion.isError && !missing && !rejected ? (
        <p className="text-gray-700">Ce critère n’a pas pu être chargé.</p>
      ) : null}

      {criterion.data === undefined ? null : (
        <CriterionEditor
          // The id and a counter, never the draft's stamp - see the head of
          // this file for what keying on the stamp would cost.
          key={`${criterion.data.id}-${reload}`}
          selectName={copie === true}
          detail={criterion.data}
          viewing={viewed.data ?? null}
          onView={(summary) =>
            navigate({
              to: '/admin/criteres/$criterionId',
              params: { criterionId },
              search: { version: summary.id },
            })
          }
          onCloseView={() =>
            navigate({
              to: '/admin/criteres/$criterionId',
              params: { criterionId },
              search: {},
            })
          }
          onReload={() => {
            // The bump waits for the answer: a remount before it lands would
            // re-seed the editor from the stale draft it is escaping.
            void criterion.refetch().then(() => setReload((was) => was + 1));
          }}
        />
      )}
    </div>
  );
}
